import React, { useState, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Title, Tab, Tag, Field, Control, Button, Icon } from "rbx";
import gql from "graphql-tag";
import { useQuery, useMutation, useApolloClient } from "react-apollo";
import {
  Switch,
  useParams,
  useLocation,
  Route,
  useHistory
} from "react-router-dom";
import { ModalContext } from "../../../components/ModalProvider";
import {
  getTagColorByOrderStatus,
  calcTimeZone,
  getDifferenceByTimeZone,
  getUTCFormattedDate,
  ourToast as toast,
  runPromisesInSeries
} from "../../../utils";
import {
  SINGLE_ORDER_QUERY,
  UPDATE_ORDER_MUTATION,
  ALL_ORDERS_QUERY
} from "../../../graphql/Orders/Orders.graphql";
import { ALL_ORDER_DETAILS_QUERY } from "./components/OrderDetails";
import { ALL_ROUTE_PRODUCTS_QUERY } from "../../../graphql/Routes/RouteProducts.graphql";

import IconLink from "../../../components/IconLink";

import { ALL_SYSTEM_CODES_QUERY } from "../../../graphql/SystemCodes/SystemCodes.graphql";

import { SINGLE_ROUTE_QUERY } from "../../../graphql/Routes/Routes.graphql";

import { Confirmation } from "../../../components/modals";

import Summary from "./routes/Summary";
import Billing from "./routes/Billing";
import BolEld from "./routes/BolEld";

import {
  AddOrderDetailForm,
  VoidOrderForm,
  PrintOrderDirectionsButton
} from "../../../components/Orders";

import Authorization, {
  useAuthorization
} from "../../../components/Authorization";

import PrintBOL from "../../../components/PrintBOL";
import Loading from "../../../components/Loading";
import SummaryRouteRefreshModal from "./components/SummaryRouteRefreshModal";
import TripKeyModal from "../../../components/TripKeyModal";
import { useSystemCodes } from "../../../utils/hooks";

const BillingAuth = Authorization(["ADMIN", "FIN"]);

class CustomOrderError extends Error {}

const SIMPLE_ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const INITIAL_STATE = {
  RecId: "",
  OrderNumber: "",
  StatusRecIdRef: "",
  CustomerRecIdRef: "",
  RouteRecIdRef: "",
  LoadTypeRecIdRef: "",
  StartLocationRecIdRef: "",
  PickupLocationRecIdRef: "",
  DeliveryLocationRecIdRef: "",
  EndingLocationRecIdRef: "",
  Customer_SI: "",
  StartLoc_SI: "",
  PickupLoc_SI: "",
  DeliveryLoc_SI: "",
  EndLoc_SI: "",
  EstimatedLoad: "",
  EstimatedTravel: "",
  EstimatedUload: "",
  OrderDate: "",
  RequestedDeliveryDate: "",
  ScheduledDeliveryDate: "",
  PickupDate: "",
  BillToRecIdRef: null,
  PaymentMethodRecIdRef: "",
  CustRef: "",
  VoidDate: "",
  VoidReason: "",
  VoidNotificationContact: "",
  VoidNotificationDate: "",
  VoidNotificationBy: "",
  VoidComments: "",
  CompanyId: "",
  GrossWeight: "",
  TareWeight: "",
  NetWeight: "",
  PickupDriverRecIdRef: "",
  PickupTractorRecIdRef: "",
  PickupTrailerRecIdRef: "",
  DeliveryDriverRecIdRef: "",
  DeliveryTractorRecIdRef: "",
  DeliveryTrailerRecIdRef: "",
  PickupTimeIn: "",
  PickupTimeOut: "",
  DeliveryTimeIn: "",
  DeliveryTimeOut: "",
  HotFlag: false,
  DateExported: "",
  DateImported: "",
  BTU: "",
  Scale: "",
  LoadNumber: "",
  TankID: "",
  TryCock: "",
  PickupInches: "",
  PickupPressure: "",
  PickupTemperature: "",
  DeliveryInches: "",
  DeliveryPressure: "",
  DeliveryTemperature: "",
  BookingCodeRecIdRef: "",
  LoadedMiles: "",
  TariffRecIdRef: "",
  ChargeForSaturday: false,
  ChargeForSunday: false,
  ChargeForHoliday: false,
  BillingNotes: "",
  OrderNotes: "",
  NonBill: false,
  Cancel: false,
  PaymentTermsRecIdRef: "",
  RouteSelected: null,
  ShippersNumber: "",
  LastClockOutTime: "",
  StartDate: ""
};

const OrderPage = () => {
  const SystemCodes = useQuery(SIMPLE_ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order"],
        CodeName_in: ["OrderStatus"],
        CodeId_in: ["EXA", "NB"]
      }
    }
  });
  const history = useHistory();
  const { data: systemCodes } = useSystemCodes({
    Category: "Order",
    CodeName: "OrderStatus"
  });
  const { id } = useParams();
  const location = useLocation();
  const isAuthorized = useAuthorization(["ADMIN", "FIN"]);
  const isScrapAuthorized = useAuthorization(["ADMIN", "ORDCAN"]);

  const [States, setStates] = useState(null);
  const client = useApolloClient();
  const { setModalOpen } = useContext(ModalContext);

  const { data } = useQuery(SINGLE_ORDER_QUERY, {
    variables: { where: { RecId: id ? parseInt(id) : 0 } },
    fetchPolicy: "cache-and-network"
  });

  const isTripped = !!data?.Order?.TripKey;

  const [updateOrder, { loading }] = useMutation(UPDATE_ORDER_MUTATION, {
    refetchQueries: ["SINGLE_ORDER_QUERY", "ALL_ORDERS_QUERY"]
  });

  const [editing, setEditing] = useState(true);
  const [inputs, setInputs] = useState(INITIAL_STATE);
  const [orderDetails, setOrderDetails] = useState([]);
  const [edited, setEdited] = useState(false);

  // Lock orders that have been exported to accounting
  useEffect(() => {
    if (data && SystemCodes?.data?.SystemCodes?.length && !States) {
      const exportedToAccounting = SystemCodes.data.SystemCodes.find(
        s => s.CodeId === "EXA"
      );
      if (data.Order.StatusRecIdRef === exportedToAccounting.RecId) {
        setEditing(false);
      }
      setStates({
        ExportedToAccounting: exportedToAccounting,
        NonBillable: SystemCodes.data.SystemCodes.find(s => s.CodeId === "NB")
      });
    }
  }, [States, SystemCodes, SystemCodes.data, data]);
  const handleChangeCustomer = e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...inputs };
    updates[e.target.name] = value ? parseInt(value) : null;
    setInputs(updates);
  };

  const handleChangeRoute = async routeData => {
    if (
      routeData.RouteRecIdRef &&
      data.Order.RouteRecIdRef &&
      routeData.RouteRecIdRef !== data.Order.RouteRecIdRef
    ) {
      const { data: RouteData } = await client.query({
        query: SINGLE_ROUTE_QUERY,
        variables: {
          where: { RecId: routeData.RouteRecIdRef }
        },
        fetchPolicy: "network-only"
      });
      const { data: RouteProductData } = await client.query({
        query: ALL_ROUTE_PRODUCTS_QUERY,
        variables: {
          where: {
            RouteRecIdRef: routeData.RouteRecIdRef
          }
        }
      });
      const { data: OrderDetailData } = await client.query({
        query: ALL_ORDER_DETAILS_QUERY,
        variables: {
          where: { OrderRecIdRef: inputs.RecId, ProdCatgRecIdRef: 306 }
        }
      });

      setModalOpen(
        true,
        <SummaryRouteRefreshModal
          order={data.Order}
          orderDetails={OrderDetailData?.OrderDetails || []}
          route={RouteData?.Route}
          routeProducts={RouteProductData?.RouteProducts || []}
          onCancel={() => {
            setModalOpen(false);
          }}
          onComplete={async () => {
            setModalOpen(false);
            toast.success("Order updated successfully.");
            setEditing(false);
            setEdited(false);
          }}
        />
      );
    }
  };

  useEffect(() => {
    if (data && data.Order) {
      const updates = { ...data.Order };
      updates.OrderDate = new Date(updates.OrderDate).toCorrectDateString();
      updates.PickupDate = new Date(updates.PickupDate).toCorrectDateString();
      updates.RequestedDeliveryDate = new Date(
        updates.RequestedDeliveryDate
      ).toCorrectDateString();

      updates.ScheduledDeliveryDate = new Date(
        updates.ScheduledDeliveryDate
      ).toCorrectDateString();

      updates.LastClockOutTime = updates.LastClockOutTime
        ? new Date(updates.LastClockOutTime).toCorrectDateString()
        : "";

      updates.LastClockOutTimeFormatted = updates.LastClockOutTime
        ? calcTimeZone(
            updates.LastClockOutTime,
            updates.PickupLocation.TimeZone.CodeId
          )
        : "";

      updates.PickupDateFormatted = calcTimeZone(
        updates.PickupDate,
        updates.PickupLocation.TimeZone.CodeId
      );
      updates.RequestedDeliveryDateFormatted = calcTimeZone(
        updates.RequestedDeliveryDate,
        updates.DeliveryLocation.TimeZone.CodeId
      );
      updates.ScheduledDeliveryDateFormatted = calcTimeZone(
        updates.ScheduledDeliveryDate,
        updates.DeliveryLocation.TimeZone.CodeId
      );
      updates.PickupTimeInFormatted = updates.PickupTimeIn
        ? calcTimeZone(
            updates.PickupTimeIn,
            updates.PickupLocation.TimeZone.CodeId
          )
        : "";
      updates.PickupTimeOutFormatted = updates.PickupTimeOut
        ? calcTimeZone(
            updates.PickupTimeOut,
            updates.PickupLocation.TimeZone.CodeId
          )
        : "";
      updates.DeliveryTimeInFormatted = updates.DeliveryTimeIn
        ? calcTimeZone(
            updates.DeliveryTimeIn,
            updates.DeliveryLocation.TimeZone.CodeId
          )
        : "";
      updates.DeliveryTimeOutFormatted = updates.DeliveryTimeOut
        ? calcTimeZone(
            updates.DeliveryTimeOut,
            updates.DeliveryLocation.TimeZone.CodeId
          )
        : "";

      updates.OrderedBy = !!(updates.CreatedBy && updates.OrderDateTime);
      updates.RouteSelected = {
        value: updates.Route.RecId,
        name: "RouteRecIdRef",
        label: `${updates.Route?.RouteId} - ${updates.Route?.PickupLocation?.City}, ${updates.Route?.PickupLocation?.State} / ${updates.Route?.DeliveryLocation?.City}, ${updates.Route?.DeliveryLocation?.State}`
      };
      setInputs(updates);
    }
  }, [data]);

  const handleChange = e => {
    setEdited(true);
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    const updates = {
      [e.target.name]: value
    };

    if (e.target.name === "TareWeight") {
      updates.TareWeight = parseFloat(e.target.value);
      updates.NetWeight = inputs.GrossWeight - updates.TareWeight;
    }

    if (e.target.name === "GrossWeight") {
      updates.GrossWeight = parseFloat(e.target.value);
      updates.NetWeight = updates.GrossWeight - inputs.TareWeight;
    }

    if (e.target.name === "PickupLocation") {
      updates.PickupDateFormatted = calcTimeZone(
        inputs.PickupDate,
        e.target.value.TimeZone.CodeId
      );
    }
    if (e.target.name === "DeliveryLocation") {
      updates.RequestedDeliveryDateFormatted = calcTimeZone(
        inputs.RequestedDeliveryDate,
        e.target.value.TimeZone.CodeId
      );
      updates.ScheduledDeliveryDateFormatted = calcTimeZone(
        inputs.ScheduledDeliveryDate,
        e.target.value.TimeZone.CodeId
      );
    }
    if (e.target.name === "PickupTimeOut") {
      updates.PickupTimeOutFormatted = calcTimeZone(
        e.target.value,
        inputs.PickupLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "PickupTimeIn") {
      updates.PickupTimeInFormatted = calcTimeZone(
        e.target.value,
        inputs.PickupLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "DeliveryTimeIn") {
      updates.DeliveryTimeInFormatted = calcTimeZone(
        e.target.value,
        inputs.DeliveryLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "DeliveryTimeOut") {
      updates.DeliveryTimeOutFormatted = calcTimeZone(
        e.target.value,
        inputs.DeliveryLocation.TimeZone.CodeId
      );
    }
    if (e.target.name === "PickupTimeOutFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Pickup");
      updates.PickupTimeOut = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "PickupTimeInFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Pickup");
      updates.PickupTimeIn = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "DeliveryTimeInFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Delivery");
      updates.DeliveryTimeIn = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "DeliveryTimeOutFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Delivery");
      updates.DeliveryTimeOut = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "LoadTypeRecIdRef") {
      updates[e.target.name] = parseInt(value);
    }
    if (e.target.name === "TariffRecIdRef") {
      updates[e.target.name] = parseInt(value);
    }
    if (e.target.name === "LastClockOutTimeFormatted") {
      const date = new Date(e.target.value);
      const diference = getDifferenceByTimeZone(inputs, date, "Pickup");
      updates.LastClockOutTime = date.setHours(date.getHours() - diference);
    }
    if (e.target.name === "ShippersNumber") {
      const regex = /^[0-9]*$/;

      updates[e.target.name] = regex.test(e.target.value)
        ? e.target.value
        : e.target.value.slice(0, -1);
    }

    setInputs(prev => ({ ...prev, ...updates }));
  };

  const handleVoidCancelOrder = () => {
    setModalOpen(
      true,
      <VoidOrderForm
        OrderRecIdRef={parseInt(id)}
        inputs={inputs}
        setModalOpen={setModalOpen}
        onChange={handleChange}
      />
    );
  };

  const handleSave = async (e, goTo, printAction, voidAction) => {
    try {
      if (e) {
        e.preventDefault();
      }
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      delete obj.RouteSelected;

      const btu = obj.BTU;
      if (btu && btu < 999) {
        throw new CustomOrderError("BTU must be greater than 999");
      }

      if (obj.RequestedDeliveryDate && obj.BookingCodeRecIdRef) {
        await updateOrder({
          variables: {
            data: {
              ...obj,
              BillToRecIdRef: obj.BillToRecIdRef
                ? parseInt(obj.BillToRecIdRef)
                : null,
              CustomerRecIdRef: parseInt(obj.CustomerRecIdRef),
              RouteRecIdRef: parseInt(obj.RouteRecIdRef),
              StatusRecIdRef: parseInt(obj.StatusRecIdRef),
              EstimatedLoad: parseFloat(obj.EstimatedLoad),
              EstimatedTravel: parseFloat(obj.EstimatedTravel),
              EstimatedUnload: parseFloat(obj.EstimatedUload),
              PaymentMethodRecIdRef: parseInt(obj.PaymentMethodRecIdRef),
              StartLocationRecIdRef: obj.StartLocationRecIdRef
                ? parseInt(obj.StartLocationRecIdRef)
                : null,
              PickupLocationRecIdRef: obj.PickupLocationRecIdRef
                ? parseInt(obj.PickupLocationRecIdRef)
                : null,
              DeliveryLocationRecIdRef: obj.DeliveryLocationRecIdRef
                ? parseInt(obj.DeliveryLocationRecIdRef)
                : null,
              EndingLocationRecIdRef: obj.EndingLocationRecIdRef
                ? parseInt(obj.EndingLocationRecIdRef)
                : null,
              OrderDate: getUTCFormattedDate(inputs.OrderDate),
              PickupDate: new Date(inputs.PickupDate),
              RequestedDeliveryDate: new Date(inputs.RequestedDeliveryDate),
              ScheduledDeliveryDate: new Date(inputs.ScheduledDeliveryDate),
              PickupDriverRecIdRef: obj.PickupDriverRecIdRef
                ? parseInt(obj.PickupDriverRecIdRef)
                : null,
              PickupTractorRecIdRef: obj.PickupTractorRecIdRef
                ? parseInt(obj.PickupTractorRecIdRef)
                : null,
              PickupTrailerRecIdRef: obj.PickupTrailerRecIdRef
                ? parseInt(obj.PickupTrailerRecIdRef)
                : null,
              DeliveryDriverRecIdRef: obj.DeliveryDriverRecIdRef
                ? parseInt(obj.DeliveryDriverRecIdRef)
                : null,
              DeliveryTractorRecIdRef: obj.DeliveryTractorRecIdRef
                ? parseInt(obj.DeliveryTractorRecIdRef)
                : null,
              DeliveryTrailerRecIdRef: obj.DeliveryTrailerRecIdRef
                ? parseInt(obj.DeliveryTrailerRecIdRef)
                : null,
              PickupTimeIn: obj.PickupTimeIn
                ? new Date(obj.PickupTimeIn)
                : null,
              PickupTimeOut: obj.PickupTimeOut
                ? new Date(obj.PickupTimeOut)
                : null,
              DeliveryTimeIn: obj.DeliveryTimeIn
                ? new Date(obj.DeliveryTimeIn)
                : null,
              DeliveryTimeOut: obj.DeliveryTimeOut
                ? new Date(obj.DeliveryTimeOut)
                : null,
              GrossWeight: obj.GrossWeight ? parseInt(obj.GrossWeight) : null,
              TareWeight: obj.TareWeight ? parseInt(obj.TareWeight) : null,
              NetWeight: obj.NetWeight ? parseInt(obj.NetWeight) : null,
              PickupInches: obj.PickupInches
                ? parseInt(obj.PickupInches)
                : null,
              PickupPressure: obj.PickupPressure
                ? parseInt(obj.PickupPressure)
                : null,
              PickupTemperature: obj.PickupTemperature
                ? parseInt(obj.PickupTemperature)
                : null,
              DeliveryInches: obj.DeliveryInches
                ? parseInt(obj.DeliveryInches)
                : null,
              DeliveryPressure: obj.DeliveryPressure
                ? parseInt(obj.DeliveryPressure)
                : null,
              DeliveryTemperature: obj.DeliveryTemperature
                ? parseInt(obj.DeliveryTemperature)
                : null,
              BTU: obj.BTU ? parseInt(obj.BTU) : null,
              TankID: obj.TankID ? parseInt(obj.TankID) : null,
              Scale: obj.Scale ? parseInt(obj.Scale) : null,
              TryCock: obj.TryCock ? parseInt(obj.TryCock) : null,
              DateImported: obj.DateImported
                ? new Date(obj.DateImported)
                : null,
              DateExported: obj.DateExported
                ? new Date(obj.DateExported)
                : null,
              LoadNumber: obj.LoadNumber ? parseInt(obj.LoadNumber) : null,
              BookingCodeRecIdRef: obj.BookingCodeRecIdRef
                ? parseInt(obj.BookingCodeRecIdRef)
                : null,
              LoadedMiles: obj.LoadedMiles ? parseFloat(obj.LoadedMiles) : null,
              TariffRecIdRef: obj.TariffRecIdRef
                ? parseInt(obj.TariffRecIdRef)
                : null,
              ChargeForSaturday: obj.ChargeForSaturday,
              ChargeForSunday: obj.ChargeForSunday,
              ChargeForHoliday: obj.ChargeForHoliday,
              BillingNote: obj.BillingNote,
              OrderNotes: obj.OrderNotes,
              NonBill: !!obj.NonBill,
              VoidDate: inputs.VoidDate
                ? getUTCFormattedDate(inputs.VoidDate)
                : null,
              InvoiceDate: inputs.InvoiceDate
                ? getUTCFormattedDate(inputs.InvoiceDate)
                : null,
              PaymentTermsRecIdRef: inputs.PaymentTermsRecIdRef
                ? parseInt(inputs.PaymentTermsRecIdRef)
                : null,
              ShippersNumber: inputs.ShippersNumber,
              LastClockOutTime: inputs.LastClockOutTime
                ? new Date(inputs.LastClockOutTime)
                : null
            },
            where: { RecId: parseInt(id) }
          }
        });

        if (goTo) {
          history.push(goTo);
        } else if (voidAction) {
          handleVoidCancelOrder();
        } else if (!printAction) {
          toast.success("Order updated successfully.");
          setEditing(false);
        }
        setEdited(false);
      } else {
        if (!inputs.RequestedDeliveryDate) {
          toast.error("Requested Delivery Date is required.");
        }
        if (!inputs.BookingCodeRecIdRef) {
          toast.error("Booking Code is required.");
        }
        toast.error("Form Arguments Invalid");
      }
    } catch (err) {
      if (err instanceof CustomOrderError) {
        toast.error(err.message);
      } else {
        toast.error("Error updating Order.");
      }
    }
  };

  const toggleEditing = () => {
    setEditing(data?.Order.Status.CodeId !== "V" && !editing);
  };

  const resetStatus = () => {
    // "NB"  === "Non-Billable"
    // "CAN"=== "Cancelled"
    // "EXA" === "Exported to Accounting"
    const newStatus = {
      NB: "Cancelled",
      EXA: "Route Verified"
    }[inputs.Status.CodeId];

    setModalOpen(
      true,
      <Confirmation
        message={`Are you sure you want to reset the status back to ${newStatus}?`}
        onConfirm={async () => {
          setEditing(true);
          setModalOpen(false);
          const systemCodeData = await client.query({
            query: ALL_SYSTEM_CODES_QUERY,
            variables: {
              where: {
                Category: "Order",
                CodeName: "OrderStatus",
                CodeId: inputs.Cancel ? "CAN" : "RV"
              }
            },
            fetchPolicy: "network-only"
          });
          if (
            systemCodeData &&
            systemCodeData.data &&
            Array.isArray(systemCodeData.data.SystemCodes) &&
            systemCodeData.data.SystemCodes.length > 0
          ) {
            await updateOrder({
              variables: {
                data: {
                  StatusRecIdRef: parseInt(
                    systemCodeData.data.SystemCodes[0].RecId
                  )
                },
                where: { RecId: parseInt(id) }
              }
            });
            toast.success("Order status updated successfully.");
            setEditing(false);
          }
        }}
      />
    );
  };

  const changeTab = tab => () => {
    if (edited) {
      handleSave(`/dispatch/orders/${inputs?.RecId}/${tab}`);
    } else {
      history.push(`/dispatch/orders/${inputs?.RecId}/${tab}`);
    }
  };

  useEffect(() => {
    if (data?.Order?.StatusRecIdRef) {
      setEditing(false);
    }
  }, [data]);

  const [tripLoading, setTripLoading] = useState(false);

  const handleTripClick = async () => {
    try {
      setTripLoading(true);
      if (isTripped) {
        const updateData = {
          BayNumber: null,
          Trip: null,
          Leg: null,
          TripKey: null,
          ComplexTrip: false,
          PickupDriverRecIdRef: null,
          DeliveryDriverRecIdRef: null,
          PickupTractorRecIdRef: null,
          DeliveryTractorRecIdRef: null,
          PickupTrailerRecIdRef: null,
          DeliveryTrailerRecIdRef: null
        };

        const unscheduled = systemCodes.find(sc => sc.CodeId === "U");

        if (unscheduled) {
          updateData.StatusRecIdRef = unscheduled.RecId;
        }

        const { data: OrdersData } = await client.query({
          query: ALL_ORDERS_QUERY,
          variables: {
            where: {
              TripKey: data?.Order?.TripKey
            },
            orderByMulti: ["Leg_ASC"]
          },
          fetchPolicy: "network-only"
        });
        const orders = OrdersData?.Orders;

        const o = data?.Order;

        if (orders && orders.length < 20) {
          await runPromisesInSeries(
            orders.map(x => () =>
              client.mutate({
                mutation: UPDATE_ORDER_MUTATION,
                variables: {
                  where: {
                    RecId: parseInt(x.RecId, 10)
                  },
                  data:
                    o.Leg === 1
                      ? updateData
                      : {
                          Trip:
                            x.Trip && x.Leg && x.RecId === o.RecId
                              ? null
                              : x.Trip,
                          Leg:
                            x.Trip && x.Leg && x.RecId === o.RecId
                              ? null
                              : x.Leg - (x.Leg > o.Leg ? 1 : 0),
                          TripKey: x.RecId === o.RecId ? null : x.TripKey,
                          ComplexTrip:
                            x.RecId === o.RecId ? false : x.ComplexTrip,
                          BayNumber: x.RecId === o.RecId ? null : x.BayNumber,
                          StatusRecIdRef:
                            x.RecId === o.RecId
                              ? unscheduled.RecId
                              : x.StatusRecIdRef,
                          PickupDriverRecIdRef:
                            x.RecId === o.RecId ? null : x.PickupDriverRecIdRef,
                          DeliveryDriverRecIdRef:
                            x.RecId === o.RecId
                              ? null
                              : x.DeliveryDriverRecIdRef,
                          PickupTractorRecIdRef:
                            x.RecId === o.RecId
                              ? null
                              : x.PickupTractorRecIdRef,
                          DeliveryTractorRecIdRef:
                            x.RecId === o.RecId
                              ? null
                              : x.DeliveryTractorRecIdRef,
                          PickupTrailerRecIdRef:
                            x.RecId === o.RecId
                              ? null
                              : x.PickupTrailerRecIdRef,
                          DeliveryTrailerRecIdRef:
                            x.RecId === o.RecId
                              ? null
                              : x.DeliveryTrailerRecIdRef
                        }
                }
              })
            )
          );
        }

        await client.mutate({
          mutation: UPDATE_ORDER_MUTATION,
          variables: {
            where: {
              RecId: parseInt(id, 10)
            },
            data: updateData
          },
          refetchQueries: [
            {
              query: SINGLE_ORDER_QUERY,
              variables: {
                where: {
                  RecId: parseInt(id, 10)
                }
              }
            }
          ]
        });
        toast.success("Order removed from Trip successfully.");
      } else {
        setModalOpen(
          true,
          <TripKeyModal
            orderId={id}
            onComplete={() => setModalOpen(false, "")}
          />
        );
      }
    } catch (err) {
      toast.error("Whoops! Something went wrong. Please contact support.");
    } finally {
      setTripLoading(false);
    }
  };

  if (loading || !data?.Order || SystemCodes?.loading || !States)
    return <Loading />;

  const isTripButtonDisabled = tripLoading;
  const isTripButtonVisible = data?.Order?.Status?.ListOrder <= 2;

  return (
    <div className="order-page">
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={3}>
            Order #{inputs?.OrderNumber}
          </Title>
          <Tag
            color={
              data?.Order?.Status?.CodeDescription
                ? getTagColorByOrderStatus(data.Order.Status.CodeDescription)
                : "danger"
            }
          >
            <b>
              {(
                data?.Order?.Status?.CodeDescription ?? "UNKNOWN"
              ).toUpperCase()}
            </b>
          </Tag>
          <div className="page-head-start--subheader">
            <IconLink
              icon="address-card"
              label={inputs?.Customer?.Name}
              to={`/finance/customers/${inputs?.CustomerRecIdRef}/details`}
            />
            <IconLink
              icon="route"
              label={inputs?.Route?.RouteId}
              to={`/dispatch/routes/${inputs?.RouteRecIdRef}/details`}
            />
          </div>
        </div>
        <div className="page-head-end">
          {location.pathname.includes("details") ? (
            <Field kind="group">
              <Control>
                <Button
                  color="success"
                  size="small"
                  onClick={() =>
                    setModalOpen(
                      true,
                      <AddOrderDetailForm
                        OrderRecIdRef={id}
                        setModalOpen={setModalOpen}
                      />
                    )
                  }
                >
                  <Icon>
                    <FontAwesomeIcon icon="plus" />
                  </Icon>
                  <span>Add Order Detail</span>
                </Button>
              </Control>
            </Field>
          ) : (
            <Field kind="group">
              {isTripButtonVisible && (
                <Control>
                  <Button
                    color="info"
                    disabled={isTripButtonDisabled}
                    size="small"
                    state={tripLoading ? "loading" : ""}
                    onClick={handleTripClick}
                  >
                    <Icon>
                      <FontAwesomeIcon icon={isTripped ? "minus" : "plus"} />
                    </Icon>
                    <span>
                      {[isTripped && "Un", "Trip"].filter(Boolean).join("")}
                    </span>
                  </Button>
                </Control>
              )}
              <Control>
                <PrintOrderDirectionsButton
                  edited={edited}
                  handleSave={handleSave}
                  orderRecId={parseInt(id)}
                />
              </Control>
              <Control>
                <PrintBOL
                  edited={edited}
                  filters={{ RecId: parseInt(id) }}
                  handleSave={handleSave}
                />
              </Control>
              {isScrapAuthorized && (
                <Control>
                  <Button
                    color="danger"
                    size="small"
                    onClick={e =>
                      edited
                        ? handleSave(e, null, false, true)
                        : handleVoidCancelOrder()
                    }
                  >
                    <Icon>
                      <FontAwesomeIcon icon="exclamation-circle" />
                    </Icon>
                    <span>Void / Cancel</span>
                  </Button>
                </Control>
              )}

              <Control>
                <Button
                  color="primary"
                  disabled={
                    [
                      States.ExportedToAccounting.RecId,
                      States.NonBillable.RecId
                    ].includes(data.Order.StatusRecIdRef) || editing
                  }
                  size="small"
                  onClick={toggleEditing}
                >
                  <Icon>
                    <FontAwesomeIcon icon="edit" />
                  </Icon>
                  <span>Edit</span>
                </Button>
              </Control>
              <Control>
                <Button
                  color="success"
                  disabled={!editing}
                  size="small"
                  onClick={e => handleSave(e, false)}
                >
                  <Icon>
                    <FontAwesomeIcon icon="save" />
                  </Icon>
                  <span>Save</span>
                </Button>
              </Control>
            </Field>
          )}
        </div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("summary")}
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={changeTab("summary")}
        >
          <span>Summary</span>
        </Tab>
        <Tab
          active={location.pathname.includes("BOL-ELD")}
          as={Button}
          style={{ cursor: "pointer" }}
          onClick={changeTab("BOL-ELD")}
        >
          <span>BOL/ELD</span>
        </Tab>
        {isAuthorized && (
          <Tab
            active={location.pathname.includes("billing")}
            as={Button}
            style={{ cursor: "pointer" }}
            onClick={changeTab("billing")}
          >
            <span>Billing</span>
          </Tab>
        )}
      </Tab.Group>
      <Switch>
        <Route path="/dispatch/orders/:id/summary">
          <Summary
            editing={
              data.Order.StatusRecIdRef !== States.ExportedToAccounting.RecId &&
              editing
            }
            handleChangeCustomer={handleChangeCustomer}
            handleChangeRoute={handleChangeRoute}
            order={inputs}
            orderDetails={orderDetails}
            setOrderDetails={setOrderDetails}
            onChange={handleChange}
            onTimeFramesChange={times => setInputs({ ...inputs, ...times })}
          />
        </Route>
        <Route path="/dispatch/orders/:id/billing">
          {BillingAuth(Billing, {
            order: inputs,
            onChange: handleChange,
            editing,
            resetStatus
          })}
        </Route>
        {/* <Locations
          path="locations"
          order={inputs}
          onChange={handleChange}
          editing={editing}
        /> */}
        <Route path="/dispatch/orders/:id/BOL-ELD">
          <BolEld
            disabled={!editing}
            editing={
              data?.Order?.Status?.CodeId === "IMP" ||
              data?.Order?.Status?.CodeId === "RV"
                ? editing
                : false
            }
            order={inputs}
            onChange={handleChange}
          />
        </Route>
      </Switch>
    </div>
  );
};

OrderPage.propTypes = {};

export default OrderPage;
